import { PropertyUnit } from "@/models/properties.model.ts";
import { ReadAllResponse, uuid } from "@/interfaces";
import { store } from "@/lib/store.ts";
import { requirePermission } from "@/lib/permissions";
import { api } from "@/lib/api";
import { everyProperty } from "@/repositories/property-records";
import { everyUnit, toPropertyUnit, toUnitOption, toUnitRequest, type UnitForm, type UnitOption } from "@/repositories/unit-records";
import type { PropertyListQuery } from "@shyne/shared/api";

export type { UnitOption };

export interface PropertyOption {
  id: uuid;
  name: string;
}

async function propertyOptions(query: Partial<PropertyListQuery>): Promise<PropertyOption[]> {
  const properties = await everyProperty(api.properties.list, { ...query, sort: "name", order: "asc" });
  return properties.map(({ id, name }) => ({ id, name }));
}

const actingOrganization = () => store.getState().organization.activeOrgId ?? undefined;

export const PropertyUnitsRepository = {
  async getUnitsForProperty(
    propertyId: uuid,
    unitNumber: string | null = null,
    page = 1,
    pageSize = 10,
    sortDirection: "asc" | "desc" = "asc"
  ): Promise<ReadAllResponse<PropertyUnit>> {
    await requirePermission("property_units.view");

    const search = unitNumber?.trim();
    const { items, total } = await api.units.list(propertyId, {
      actingOrganization: actingOrganization(),
      ...(search ? { search } : {}),
      order: sortDirection,
      page,
      pageSize,
    });
    return { data: items.map(toPropertyUnit), count: total };
  },

  async createUnit(unit: UnitForm & { property: uuid }): Promise<UnitOption> {
    await requirePermission("property_units.create");
    return toUnitOption(await api.units.create(unit.property, toUnitRequest(unit)));
  },

  async updateUnit(propertyId: uuid, id: uuid, updates: UnitForm): Promise<void> {
    await requirePermission("property_units.update");
    await api.units.edit(propertyId, id, toUnitRequest(updates));
  },

  async deleteUnit(propertyId: uuid, id: uuid): Promise<void> {
    await requirePermission("property_units.delete");
    await api.units.delete(propertyId, id);
  },

  async listForOrg(): Promise<PropertyOption[]> {
    await requirePermission("property_units.view");

    const orgId = store.getState().organization.activeOrgId;
    if (!orgId) return [];

    return propertyOptions({ actingOrganization: orgId, customer: orgId });
  },

  async listForVendor(vendorId: uuid): Promise<PropertyOption[]> {
    await requirePermission("property_units.view");

    const orgId = store.getState().organization.activeOrgId;
    if (!orgId) return [];

    return propertyOptions({ actingOrganization: orgId, customer: orgId, vendor: vendorId });
  },

  async listUnits(propertyId: uuid): Promise<UnitOption[]> {
    await requirePermission("property_units.view");

    const orgId = actingOrganization();
    if (!orgId) return [];

    return (await everyUnit(api.units.list, propertyId, { actingOrganization: orgId })).map(toUnitOption);
  },
};
